import { useState } from 'react';
import { Pencil, PlusCircle, Trash2 } from 'lucide-react';

const sec = (all, n) => all?.[n] || all?.[String(n)] || {};
const accts = (all, n) => (Array.isArray(sec(all, n).accounts) ? sec(all, n).accounts : []);
const label = (a) => a.creditor_name || a.creditorName || a.name || 'Account';
const TYPES = { CREDIT_CARD: 'Credit card', LOAN_LEASE: 'Personal / business loan', AUTO_LOAN: 'Auto loan or lease', PHONE: 'Mobile phone', UTILITY: 'Utility', RENTAL: 'Rental', MEDICAL: 'Medical', STUDENT_LOAN: 'Student loan', REAL_ESTATE: 'Real estate loan', INSURANCE: 'Insurance', OTHER: 'Other identity theft' };
const slotType = (all, n) => { const t = sec(all, n).fraudType || sec(all, n).fraud_type; return t ? (TYPES[t] || t) : 'No type set'; };
const acctType = (a) => (a.category ? String(a.category).replace(/_/g, ' ').toLowerCase() : '');

// Three ways to edit a saved FTC report. Both "add" choices go to the AI extractor / paste step next.
export default function FtcEditChoiceModal({ sections, onEdit, onAppend, onRemoveAndAdd, onCancel }) {
  const [removing, setRemoving] = useState(false);
  const [pick, setPick] = useState({}); // "section:index" → true
  const open = [1, 2, 3, 4, 5].filter((n) => !accts(sections, n).length);
  const toggle = (k) => setPick((p) => ({ ...p, [k]: !p[k] }));
  const toggleSection = (n) => {
    const keys = accts(sections, n).map((_, i) => `${n}:${i}`);
    const all = keys.every((k) => pick[k]);
    setPick((p) => ({ ...p, ...Object.fromEntries(keys.map((k) => [k, !all])) }));
  };
  const chosen = Object.keys(pick).filter((k) => pick[k]);
  const allKeys = [1, 2, 3, 4, 5].flatMap((n) => accts(sections, n).map((_, i) => `${n}:${i}`));
  const Opt = ({ id, cls, Icon, title, sub, onClick, disabled }) => (
    <button type="button" disabled={disabled} onClick={onClick} data-testid={id}
      className={`w-full text-left px-4 py-3 rounded-lg text-white transition disabled:opacity-40 disabled:cursor-not-allowed ${cls}`}>
      <div className="text-sm font-bold flex items-center gap-2"><Icon className="w-4 h-4" />{title}</div>
      <div className="text-[11px] opacity-85 mt-0.5">{sub}</div>
    </button>
  );
  return (
    <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4" data-testid="ftc-edit-intent-modal">
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl border border-zinc-700 bg-zinc-900 p-5 shadow-2xl">
        <h3 className="text-lg font-bold text-white mb-1">How do you want to edit this report?</h3>
        <p className="text-xs text-zinc-400 mb-4">This client already has a saved FTC report. Pick one of the three:</p>
        <div className="space-y-2">
          <Opt id="ftc-edit-intent-append" cls="bg-emerald-600 hover:bg-emerald-500" Icon={PlusCircle} onClick={() => onAppend(open)}
            title="1. Keep all old accounts + add new ones"
            sub="Old accounts stay in their sections — you can still edit or delete any of them. Add new ones with the AI extractor and pick the section for each." />
          <Opt id="ftc-edit-intent-remove" cls="bg-amber-600 hover:bg-amber-500" Icon={Trash2} onClick={() => setRemoving((v) => !v)}
            title="2. Remove accounts, then add new ones"
            sub="Pick the accounts to take off. Then add new ones with the AI extractor or paste — they fill the freed slots." />
          {removing && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-950/20 p-3 space-y-2" data-testid="ftc-edit-remove-picker">
              <button type="button" onClick={() => onRemoveAndAdd(allKeys)} disabled={!allKeys.length} data-testid="ftc-edit-remove-all"
                className="w-full py-2 rounded-md bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white text-xs font-bold">
                Remove ALL {allKeys.length} accounts — start with 5 empty slots
              </button>
              <p className="text-[11px] text-zinc-400">Or tick only the ones to take off:</p>
              {[1, 2, 3, 4, 5].map((n) => (
                <div key={n} className="rounded-md border border-zinc-700 bg-zinc-900/60 p-2" data-testid={`ftc-edit-slot-${n}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-bold text-white">Slot {n} <span className="font-normal text-amber-300">· {accts(sections, n).length ? slotType(sections, n) : 'Empty'}</span></span>
                    {accts(sections, n).length > 0 && (
                      <button type="button" onClick={() => toggleSection(n)} className="text-[11px] text-amber-300 underline" data-testid={`ftc-edit-remove-section-${n}`}>
                        Select all ({accts(sections, n).length})
                      </button>
                    )}
                  </div>
                  {accts(sections, n).map((a, i) => (
                    <label key={i} className="flex items-start gap-2 text-xs text-zinc-200 py-0.5">
                      <input type="checkbox" className="mt-0.5" checked={!!pick[`${n}:${i}`]} onChange={() => toggle(`${n}:${i}`)} data-testid={`ftc-edit-remove-${n}-${i}`} />
                      <span>{label(a)}{a.balance ? ` · $${a.balance}` : ''}{a.date_opened ? ` · ${a.date_opened}` : ''}{acctType(a) ? <span className="text-zinc-400"> · {acctType(a)}</span> : null}</span>
                    </label>
                  ))}
                </div>
              ))}
              <button type="button" disabled={!chosen.length} onClick={() => onRemoveAndAdd(chosen)} data-testid="ftc-edit-remove-confirm"
                className="w-full py-2 rounded-md bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-800 disabled:text-zinc-500 text-black text-xs font-bold">
                {chosen.length ? `Remove ${chosen.length} account${chosen.length > 1 ? 's' : ''} and add new ones` : 'Tick the accounts to remove'}
              </button>
            </div>
          )}
          <Opt id="ftc-edit-intent-edit" cls="bg-blue-600 hover:bg-blue-500" Icon={Pencil} onClick={onEdit}
            title="3. Edit report"
            sub="Change anything already on the report — personal info, amounts, dates, creditor names, sections, statement." />
          <button type="button" onClick={onCancel} className="w-full text-center text-[11px] text-zinc-500 hover:text-zinc-300 mt-1 py-1" data-testid="ftc-edit-intent-cancel">
            Never mind — just edit as-is
          </button>
        </div>
      </div>
    </div>
  );
}
